/**
 * Offer API client for managing investor offers on properties
 */

import { authenticatedRequest } from '@/common/api/client'

/**
 * Paginated response wrapper from v2 API
 */
export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    page_size: number
    total_count: number
    total_pages: number
    has_more: boolean
  }
}

/**
 * Build a JSON filter expression for the v2 API
 */
function buildFilterJson(
  filters: Array<{ field: string; op: string; value: unknown }>
): string | null {
  if (filters.length === 0) return null
  if (filters.length === 1) return JSON.stringify(filters[0])
  return JSON.stringify({ and: filters })
}

/**
 * Offer status enum values
 */
export type OfferStatus = 'pending' | 'engaged' | 'accepted' | 'rejected' | 'withdrawn'

/**
 * Closing timeline enum values
 */
export type ClosingTimeline = '10_days' | '14_days' | '30_days' | 'flexible'

export type OfferSource = 'manual' | 'auto_bid'

/**
 * Offer creation request data
 */
export interface OfferCreate {
  property_id: string
  offer_price: number
  /** Earnest money deposit as a percentage (0-100, up to 2 decimal places) */
  earnest_money_deposit?: number
  /** Closing timeline */
  closing_timeline?: ClosingTimeline
  /** Notes visible to all parties */
  notes?: string
  /** Private notes only visible to investor and admins (not agents) */
  private_notes?: string
}

/**
 * Offer response data
 */
export interface OfferResponse {
  id: string
  property_id: string
  investor_id: string
  offer_price: number
  /** Earnest money deposit as a percentage (0-100, up to 2 decimal places) */
  earnest_money_deposit?: number
  /** Closing timeline */
  closing_timeline?: ClosingTimeline
  /** Notes visible to all parties */
  notes?: string
  /** Private notes only visible to investor and admins (not agents) */
  private_notes?: string
  status: OfferStatus
  /** How the offer was created; absent when the request didn't select it */
  source?: OfferSource
  created_at: string
  updated_at: string
  updated_by?: string
}

/**
 * Investor profile info (from investor relationship)
 */
export interface InvestorInfo {
  user_id: string
  first_name: string
  last_name: string
  public_email?: string | null
  company_name?: string | null
}

/**
 * Property address info (from property.address relationship)
 */
export interface PropertyAddressInfo {
  id: string
  street_address: string
  unit_number?: string | null
  city: string
  state: string
  zipcode: string
  full_address: string
}

/**
 * Property info (from property relationship)
 */
export interface PropertyInfo {
  id: string
  market: string
  status: string
  market_value: number
  property_type: string
  beds?: number | null
  baths?: number | null
  sqft?: number | null
  address?: PropertyAddressInfo | null
  offer_deadline?: string | null
  thumbnail?: string | null
}

/**
 * Offer with property details response data
 */
/**
 * The other party's contact details for the viewer (MRG-1525/1545). Outside the
 * negotiation pilot the agent always gets the investor's public email and phone.
 */
export interface OfferContact {
  name: string | null
  email: string | null
  phone: string | null
}

export interface OfferWithPropertyResponse extends OfferResponse {
  /** @deprecated Use property.address.full_address instead */
  property_address?: string
  /** @deprecated Use property.market instead */
  property_market?: string
  /** @deprecated Use property.status instead */
  property_status?: string
  investor?: InvestorInfo
  property?: PropertyInfo
  contact?: OfferContact | null
}

/**
 * Create a new offer on a property
 *
 * @param token - JWT access token
 * @param data - Offer creation data
 * @returns Created offer data
 * @throws ApiError if request fails (404 if property not found, 400 if property not available)
 */
export async function createOffer(
  token: string,
  data: OfferCreate
): Promise<OfferResponse> {
  return authenticatedRequest<OfferResponse>('/offers', token, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Get list of offers
 *
 * For investors: returns their own offers
 * For agents: returns offers on their properties
 * For admins: returns all offers
 *
 * @param token - JWT access token
 * @param status - Optional status filter
 * @param propertyIds - Optional property IDs filter (max 100)
 * @returns List of offers with property details
 * @throws ApiError if request fails
 */
export async function getOffers(
  token: string,
  status?: OfferStatus,
  propertyIds?: string[]
): Promise<OfferWithPropertyResponse[]> {
  const params = new URLSearchParams()

  // Build filter conditions
  const filters: Array<{ field: string; op: string; value: unknown }> = []

  if (status) {
    filters.push({ field: 'status', op: 'eq', value: status })
  }

  if (propertyIds && propertyIds.length > 0) {
    filters.push({ field: 'property_id', op: 'in', value: propertyIds })
  }

  const filterJson = buildFilterJson(filters)
  if (filterJson) {
    params.append('filter', filterJson)
  }

  const queryString = params.toString()
  const url = queryString ? `/offers?${queryString}` : '/offers'
  const response =
    await authenticatedRequest<PaginatedResponse<OfferWithPropertyResponse>>(
      url,
      token
    )
  return response.data
}

/**
 * Parameters for listing offers with full query capabilities
 */
export interface ListOffersParams {
  /** Page number (1-indexed) */
  page?: number
  /** Items per page (max 100) */
  pageSize?: number
  /** Field to sort by */
  sortBy?: string
  /** Sort direction */
  sortDir?: 'asc' | 'desc'
  /** Filter by status (single) */
  status?: OfferStatus
  /** Filter by statuses (multiple) */
  statuses?: OfferStatus[]
  /** Filter by property IDs */
  propertyIds?: string[]
  /** Search query (searches property address) */
  search?: string
  /** Additional fields to select (e.g., 'investor') */
  select?: string[]
  /**
   * MRG-281: when true, the server returns hardcoded training fixtures.
   * The flag is silently ignored unless the caller is an external agent
   * who hasn't completed training. UI code derives this from
   * `useUserProfile().needsTraining` plus `agent_type === 'external'`.
   */
  training_mode?: boolean
}

/**
 * List offers with full query capabilities (pagination, sorting, filtering, search).
 *
 * @param token - JWT access token
 * @param params - Query parameters
 * @returns Paginated response with offers
 * @throws ApiError if request fails
 */
export async function listOffers(
  token: string,
  params: ListOffersParams = {}
): Promise<PaginatedResponse<OfferWithPropertyResponse>> {
  const urlParams = new URLSearchParams()

  // Pagination params
  if (params.page !== undefined) {
    urlParams.append('page', String(params.page))
  }
  if (params.pageSize !== undefined) {
    urlParams.append('pageSize', String(params.pageSize))
  }

  // Sort params
  if (params.sortBy) {
    urlParams.append('sortBy', params.sortBy)
  }
  if (params.sortDir) {
    urlParams.append('sortDir', params.sortDir)
  }

  // Search param (server-side property address search)
  if (params.search) {
    urlParams.append('search', params.search)
  }

  // Select param (additional fields to include)
  if (params.select && params.select.length > 0) {
    urlParams.append('select', params.select.join(','))
  }

  // Build filter conditions
  const filters: Array<{ field: string; op: string; value: unknown }> = []

  if (params.statuses && params.statuses.length > 0) {
    filters.push({ field: 'status', op: 'in', value: params.statuses })
  } else if (params.status) {
    filters.push({ field: 'status', op: 'eq', value: params.status })
  }
  if (params.propertyIds && params.propertyIds.length > 0) {
    filters.push({ field: 'property_id', op: 'in', value: params.propertyIds })
  }

  const filterJson = buildFilterJson(filters)
  if (filterJson) {
    urlParams.append('filter', filterJson)
  }

  // MRG-281 training mode (server returns fixtures for in-training external agents)
  if (params.training_mode) {
    urlParams.append('training_mode', 'true')
  }

  const queryString = urlParams.toString()
  const url = queryString ? `/offers?${queryString}` : '/offers'
  return authenticatedRequest<PaginatedResponse<OfferWithPropertyResponse>>(
    url,
    token
  )
}

export const OFFER_DEFAULT_SELECT = ['@all_fields', 'investor.@all_fields', 'property.address', 'property.thumbnail']

/**
 * Get a specific offer by ID
 *
 * @param token - JWT access token
 * @param id - Offer ID
 * @returns Offer data with property details
 * @throws ApiError if request fails (404 if offer not found, 403 if no permission)
 */
export async function getOffer(
  token: string,
  id: string
): Promise<OfferWithPropertyResponse> {
  // Same fields as the offers list, so a deep-linked detail dialog shows the investor and terms
  const params = new URLSearchParams({ select: OFFER_DEFAULT_SELECT.join(',') })
  return authenticatedRequest<OfferWithPropertyResponse>(`/offers/${id}?${params}`, token)
}

/**
 * Withdraw a pending offer
 *
 * Only the investor who created the offer can withdraw it.
 * Only pending offers can be withdrawn.
 *
 * @param token - JWT access token
 * @param id - Offer ID
 * @returns Updated offer data
 * @throws ApiError if request fails (400 if not pending, 403 if no permission, 409 if concurrent modification)
 */
export async function withdrawOffer(
  token: string,
  id: string
): Promise<OfferResponse> {
  return authenticatedRequest<OfferResponse>(`/offers/${id}/withdraw`, token, {
    method: 'PATCH',
  })
}

/**
 * Accept a pending offer (property owner only)
 *
 * Only the property owner (agent) can accept offers on their properties.
 * Only pending offers can be accepted.
 *
 * @param token - JWT access token
 * @param id - Offer ID
 * @returns Updated offer data
 * @throws ApiError if request fails (400 if not pending, 403 if no permission, 409 if concurrent modification)
 */
export async function acceptOffer(
  token: string,
  id: string
): Promise<OfferResponse> {
  return authenticatedRequest<OfferResponse>(`/offers/${id}/accept`, token, {
    method: 'PATCH',
  })
}

/**
 * Reject a pending offer (property owner only)
 *
 * Only the property owner (agent) can reject offers on their properties.
 * Only pending offers can be rejected.
 *
 * @param token - JWT access token
 * @param id - Offer ID
 * @returns Updated offer data
 * @throws ApiError if request fails (400 if not pending, 403 if no permission, 409 if concurrent modification)
 */
export async function rejectOffer(
  token: string,
  id: string
): Promise<OfferResponse> {
  return authenticatedRequest<OfferResponse>(`/offers/${id}/reject`, token, {
    method: 'PATCH',
  })
}

/**
 * Engage a pending offer (property owner only)
 *
 * Signals interest to the investor and enables counter-offer negotiation.
 * Only pending offers can be engaged.
 */
export async function engageOffer(
  token: string,
  id: string
): Promise<OfferResponse> {
  return authenticatedRequest<OfferResponse>(`/offers/${id}/engage`, token, {
    method: 'PATCH',
  })
}

/**
 * Counter-offer negotiation entry
 */
export interface NegotiationEntry {
  id: number
  offer_id: string
  proposed_by: string
  role: 'agent' | 'investor'
  offer_price: number
  earnest_money_deposit?: number | null
  closing_timeline?: ClosingTimeline | null
  notes?: string | null
  created_at: string
}

/**
 * Counter-offer creation data
 */
export interface CounterOfferCreate {
  offer_price: number
  earnest_money_deposit?: number
  closing_timeline?: ClosingTimeline
  notes?: string
}

/**
 * Submit a counter-offer on an engaged offer
 */
export async function submitCounterOffer(
  token: string,
  offerId: string,
  data: CounterOfferCreate
): Promise<NegotiationEntry> {
  return authenticatedRequest<NegotiationEntry>(
    `/offers/${offerId}/negotiations`,
    token,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  )
}

/**
 * Get negotiation history for an offer
 */
export async function getNegotiations(
  token: string,
  offerId: string
): Promise<NegotiationEntry[]> {
  return authenticatedRequest<NegotiationEntry[]>(
    `/offers/${offerId}/negotiations`,
    token
  )
}

/**
 * Get investor's offer on a specific property
 *
 * This endpoint is only available to investors and returns their offer
 * on the specified property, if one exists.
 *
 * @param token - JWT access token
 * @param propertyId - Property ID
 * @returns Offer data
 * @throws ApiError if request fails (404 if no offer exists, 403 if not an investor)
 */
export async function getMyOfferOnProperty(
  token: string,
  propertyId: string
): Promise<OfferResponse> {
  return authenticatedRequest<OfferResponse>(
    `/properties/${propertyId}/my-offer`,
    token
  )
}

/**
 * Deal viability tiers, keyed on the offer as a share of the seller's floor price.
 */
export type ViabilityTier = 't1' | 't2' | 't3'

/**
 * What the odds were computed against.
 *
 * `floor` wherever the seller shares their floor price — it is the thing the investor is
 * actually bidding against, and the copy can name it. `avm` everywhere else: the House Canary
 * valuation is visible to investors and checkable against their own, which a withheld floor is
 * not, and it carries signal the floor does not.
 */
export type ViabilityBasis = 'floor' | 'avm'

/**
 * How likely one prospective offer is to reach a conversation with the seller.
 *
 * Every field beyond `show` is null unless the tier's copy uses it, so a value that is
 * present is a value the investor may see. `percent_below_floor` and `nudge_amount` arrive
 * only when the seller shares their floor price — either would otherwise combine with the
 * offer amount to give away a floor the investor was never shown.
 */
export interface OfferViability {
  /** False when nothing should be rendered — invalid amount, missing or junk floor. */
  show: boolean
  tier?: ViabilityTier | null
  /** What the odds are computed against. Selects the copy as well as the figures. */
  basis?: ViabilityBasis | null
  /** Whether the seller shares their floor price, selecting the copy variant. */
  floor_shared?: boolean | null
  /** Chance of a conversation at this offer, whole percent. T1 and T2. */
  current_pct?: number | null
  /** Chance at the seller's floor. T1, and T2 when the nudge is named. */
  target_pct?: number | null
  /** How far under the floor this offer sits. T1 with the floor shared only. */
  percent_below_floor?: number | null
  /** Dollars needed to reach the target. T2 with the floor shared only. */
  nudge_amount?: number | null
}

/**
 * Get the deal viability insight for a prospective offer amount.
 *
 * Computed server-side: `floor_price` is gated on `share_floor_price`, so for most
 * properties the browser never receives the floor and cannot derive the ratio itself.
 *
 * @param token - JWT access token
 * @param propertyId - Property the offer would be on
 * @param offerPrice - Prospective offer amount in whole dollars
 */
export async function getOfferViability(
  token: string,
  propertyId: string,
  offerPrice: number
): Promise<OfferViability> {
  return authenticatedRequest<OfferViability>(
    `/properties/${propertyId}/offer-viability?offer_price=${encodeURIComponent(offerPrice)}`,
    token
  )
}
